/**
 * PredictionScorecard renderer
 *
 * Renders the Prediction Scorecard report data: standings for the three
 * contenders (GM Projection, Model A Run-Rate + Trend, Model B Holt-Winters)
 * and per-location tables of monthly predictions vs actuals, with the
 * closest prediction highlighted in each scored month.
 *
 * Data shape mirrors PredictionScorecardData from
 * lambda/reports/executors/prediction-scorecard.ts.
 */

import React, { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

export type ContenderId = 'GM' | 'A' | 'B';

export interface ScorecardStanding {
  id: ContenderId;
  label: string;
  blurb: string;
  grossErrorPct: number | null;
  unitsErrorPct: number | null;
  monthlyWins: number;
}

export interface ScorecardSeriesData {
  deptKey: string;
  dept: string;
  metricKey: string;
  metric: string;
  kind: 'GROSS' | 'UNITS';
  gm: number[];
  a: number[] | null;
  b: number[] | null;
  actuals: (number | null)[];
  winners: (ContenderId | 'TIE' | null)[];
}

export interface ScorecardLocationData {
  id: number;
  name: string;
  wins: Record<ContenderId, number>;
  series: ScorecardSeriesData[];
}

export interface PredictionScorecardData {
  year: number;
  scoredMonths: number[];
  preliminaryMonth: number | null;
  cellsScored: number;
  cellsTotal: number;
  leader: ContenderId | null;
  standings: ScorecardStanding[];
  locations: ScorecardLocationData[];
  excludedLocations: Array<{ id: number; name: string }>;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Contender accent styling (app palette): GM amber, Model A emerald, Model B blue
const CONTENDER_STYLE: Record<ContenderId, { text: string; winBg: string; cardBorder: string; dot: string }> = {
  GM: { text: 'text-amber-700', winBg: 'bg-amber-100', cardBorder: 'border-amber-400', dot: 'bg-amber-600' },
  A: { text: 'text-emerald-700', winBg: 'bg-emerald-100', cardBorder: 'border-emerald-400', dot: 'bg-emerald-600' },
  B: { text: 'text-blue-700', winBg: 'bg-blue-100', cardBorder: 'border-blue-400', dot: 'bg-blue-600' },
};

const fmtValue = (v: number, kind: 'GROSS' | 'UNITS'): string =>
  kind === 'GROSS' ? `$${v.toLocaleString('en-US')}` : v.toLocaleString('en-US');

const fmtPct = (v: number | null): string => (v === null ? '—' : `${(v * 100).toFixed(1)}%`);

const contenderRowLabel = (id: ContenderId): string => (id === 'GM' ? 'GM' : `Model ${id}`);

const SeriesTable: React.FC<{ series: ScorecardSeriesData; preliminaryMonth: number | null }> = ({
  series,
  preliminaryMonth,
}) => {
  const contenders: Array<{ id: ContenderId; values: number[] | null }> = [
    { id: 'GM', values: series.gm },
    { id: 'A', values: series.a },
    { id: 'B', values: series.b },
  ];

  return (
    <div className="mb-5 last:mb-0">
      <div className="flex items-baseline gap-2 mb-1">
        <h4 className="text-sm font-semibold text-gray-900">{series.dept}</h4>
        <span className="text-xs text-gray-500 uppercase tracking-wide">
          {series.metric} {series.kind === 'GROSS' ? '· $' : '· units'}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left font-medium text-gray-500 py-1 pr-2 w-20"></th>
              {MONTH_LABELS.map((label, i) => (
                <th key={label} className="text-right font-medium text-gray-500 py-1 px-2 whitespace-nowrap">
                  {label}
                  {preliminaryMonth === i + 1 && <span className="text-amber-600" title="Preliminary — books not final">*</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contenders.map(({ id, values }) => (
              <tr key={id} className="border-b border-gray-100">
                <td className={`py-1 pr-2 font-semibold whitespace-nowrap ${CONTENDER_STYLE[id].text}`}>
                  {contenderRowLabel(id)}
                </td>
                {MONTH_LABELS.map((_, i) => {
                  const isWinner = series.winners[i] === id;
                  return (
                    <td
                      key={i}
                      className={`py-1 px-2 text-right tabular-nums whitespace-nowrap ${
                        isWinner ? `font-bold ${CONTENDER_STYLE[id].winBg} ${CONTENDER_STYLE[id].text}` : 'text-gray-700'
                      }`}
                    >
                      {values ? fmtValue(values[i], series.kind) : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td className="py-1 pr-2 font-semibold text-gray-900 whitespace-nowrap">Actual</td>
              {MONTH_LABELS.map((_, i) => {
                const actual = series.actuals[i];
                const isTie = series.winners[i] === 'TIE';
                return (
                  <td key={i} className="py-1 px-2 text-right tabular-nums whitespace-nowrap font-medium text-gray-900">
                    {actual === null ? <span className="text-gray-300">—</span> : fmtValue(actual, series.kind)}
                    {isTie && <span className="ml-1 text-[10px] text-gray-400 uppercase">tie</span>}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const PredictionScorecard: React.FC<{ data: PredictionScorecardData }> = ({ data }) => {
  const [openLocations, setOpenLocations] = useState<Set<number>>(
    () => new Set(data.locations.length > 0 ? [data.locations[0].id] : [])
  );
  const [allOpen, setAllOpen] = useState(false);

  const toggleLocation = (id: number) => {
    setOpenLocations(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allOpen) {
      setOpenLocations(new Set());
      setAllOpen(false);
    } else {
      setOpenLocations(new Set(data.locations.map(l => l.id)));
      setAllOpen(true);
    }
  };

  const anyScored = data.cellsScored > 0;

  return (
    <div>
      {/* Standings */}
      <div className="mb-2 flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold text-gray-900">Standings — {data.year}</h3>
        <span className="text-xs text-gray-500">
          {data.scoredMonths.length} of 12 months scored · {data.cellsScored.toLocaleString('en-US')} of{' '}
          {data.cellsTotal.toLocaleString('en-US')} cells
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {data.standings.map(c => {
          const isLeader = anyScored && data.leader === c.id;
          const style = CONTENDER_STYLE[c.id];
          return (
            <div
              key={c.id}
              className={`relative rounded-lg border p-4 bg-white ${
                isLeader ? `border-2 ${style.cardBorder} shadow-sm` : 'border-gray-200'
              }`}
            >
              {isLeader && (
                <span className={`absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${style.winBg} ${style.text}`}>
                  Leading
                </span>
              )}
              <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2 ${style.text}`}>
                <span className={`inline-block w-2.5 h-2.5 rounded-sm ${style.dot}`} />
                {c.label}
              </div>
              <div className="text-3xl font-bold text-gray-900 tabular-nums">{fmtPct(c.grossErrorPct)}</div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-0.5">
                Gross $ error (weighted, lower wins)
              </div>
              <div className="flex gap-6 mt-3 pt-3 border-t border-dashed border-gray-200 text-sm text-gray-600">
                <div>
                  <span className="block font-semibold text-gray-900">{anyScored ? c.monthlyWins : '—'}</span>
                  monthly wins
                </div>
                <div>
                  <span className="block font-semibold text-gray-900">{fmtPct(c.unitsErrorPct)}</span>
                  units error
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">{c.blurb}</p>
            </div>
          );
        })}
      </div>

      {!anyScored && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border-l-4 border-amber-400 text-sm text-amber-800 rounded-r">
          No months scored yet — scoring begins once the first month of {data.year} closes.
        </div>
      )}
      {data.preliminaryMonth !== null && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border-l-4 border-amber-400 text-sm text-amber-800 rounded-r">
          * {MONTH_LABELS[data.preliminaryMonth - 1]} actuals are preliminary — accounting adjustments can post
          through day 10 of the following month.
        </div>
      )}

      {/* Locations */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-gray-900">By Location</h3>
        <button onClick={toggleAll} className="text-xs text-blue-600 hover:text-blue-800 underline print:hidden">
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div className="space-y-3">
        {data.locations.map(loc => {
          const isOpen = openLocations.has(loc.id);
          const totalWins = loc.wins.GM + loc.wins.A + loc.wins.B;
          return (
            <div key={loc.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button
                onClick={() => toggleLocation(loc.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 print:hidden"
              >
                <ChevronRightIcon className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                <span className="flex items-center justify-center w-9 h-9 rounded-md bg-gray-100 text-sm font-bold text-gray-700 flex-shrink-0">
                  {loc.id}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{loc.name}</span>
                  <span className="block text-xs text-gray-500">{loc.series.length} series</span>
                </span>
                {totalWins > 0 && (
                  <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">
                    wins — <span className="font-semibold text-amber-700">GM {loc.wins.GM}</span> ·{' '}
                    <span className="font-semibold text-emerald-700">A {loc.wins.A}</span> ·{' '}
                    <span className="font-semibold text-blue-700">B {loc.wins.B}</span>
                  </span>
                )}
              </button>
              {/* Print always shows every location's tables */}
              <div className={`px-4 pb-4 border-t border-gray-100 pt-3 ${isOpen ? '' : 'hidden'} print:block`}>
                <div className="hidden print:block text-sm font-bold text-gray-900 mb-2">
                  {loc.id} — {loc.name}
                </div>
                {loc.series.map(s => (
                  <SeriesTable key={`${s.deptKey}|${s.metricKey}`} series={s} preliminaryMonth={data.preliminaryMonth} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {data.excludedLocations.length > 0 && (
        <p className="text-xs text-gray-400 mt-4">
          Not scorable (no {data.year} projections with sufficient history):{' '}
          {data.excludedLocations.map(l => l.name).join(', ')}
        </p>
      )}
      <p className="text-xs text-gray-400 mt-1">
        Models train only on {data.year - 3}–{data.year - 1} books — predictions are fixed for the whole year.
        Actuals load automatically from the DOC gross summary as each month's books close.
      </p>
    </div>
  );
};
